import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import AdminPagination from '../admin/AdminPagination';
import { useRegisterPortalRefresh } from '../../contexts/PortalRefreshContext';
import ApprenantPageShell from './ApprenantPageShell';
import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import { useAdminTablePagination } from '../../hooks/useAdminTablePagination';
import { fetchInscriptionEvaluations, fetchMyInscriptions } from '../../services/academiaApprenantService';
import {
  EMPTY_EVALUATION_SUMMARY,
  evaluationBadgeLabel,
  summarizeInscriptionEvaluations,
  type EvaluationInscriptionSummary,
} from '../../utils/evaluationInscriptionSummary';
import {
  buildInscriptionPaymentNavigationState,
  computeMesCoursEnrollmentStats,
  formatInscriptionStatut,
  inscriptionResteAPayer,
  isInscriptionEffectivelyEnrolled,
  isInscriptionPendingSubscriptionPayment,
  toAmount,
} from '../../utils/inscriptionPresentation';
import { formatMoney } from '../../utils/currency';

type InscriptionRow = {
  id?: string;
  statut?: string;
  matriculeSnapshot?: string;
  dateInscription?: string;
  montantTotalCdf?: number | string;
  montantPayeCdf?: number | string;
  filiere?: { libelle?: string; code?: string };
  cohorte?: { code?: string; libelle?: string; dateDebut?: string; dateFin?: string };
  lignesFrais?: Array<{ typeFrais?: string; montantCdf?: number | string; montantPayeCdf?: number | string }>;
};

const MES_COURS_PAGE_SIZE = 10;

function statutBadgeClass(statut?: string) {
  if (statut === 'CONFIRMEE' || statut === 'EN_COURS' || statut === 'TERMINEE') {
    return 'apprenant-mes-cours-card__badge--success';
  }
  if (statut === 'ANNULEE' || statut === 'DESISTEE') {
    return 'apprenant-mes-cours-card__badge--muted';
  }
  if (statut === 'PARTIELLEMENT_PAYEE') {
    return 'apprenant-mes-cours-card__badge--info';
  }
  return 'apprenant-mes-cours-card__badge--pending';
}

function formatShortDate(value?: string) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? null
    : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function MesCoursCard({
  row,
  evaluationSummary,
}: {
  row: InscriptionRow;
  evaluationSummary: EvaluationInscriptionSummary;
}) {
  const evaluationLabel = evaluationBadgeLabel(evaluationSummary);
  const total = toAmount(row.montantTotalCdf);
  const paid = toAmount(row.montantPayeCdf);
  const reste = inscriptionResteAPayer(row);
  const ratio = total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : paid > 0 ? 100 : 0;
  const canPay = reste > 0 && row.statut !== 'ANNULEE' && row.statut !== 'CONFIRMEE';
  const paymentNav = canPay ? buildInscriptionPaymentNavigationState(row) : null;
  const sessionLabel = [formatShortDate(row.cohorte?.dateDebut), formatShortDate(row.cohorte?.dateFin)]
    .filter(Boolean)
    .join(' → ');
  const inscritLe = formatShortDate(row.dateInscription);

  return (
    <article className="apprenant-mes-cours-card">
      <div className="apprenant-mes-cours-card__accent" aria-hidden />
      <div className="apprenant-mes-cours-card__inner">
        <header className="apprenant-mes-cours-card__head">
          <span className="apprenant-mes-cours-card__icon">
            <LucideSymbolIcon name="school" tone="inherit" size={22} />
          </span>
          <div className="min-w-0 flex-1">
            <h3 className="apprenant-mes-cours-card__title">{row.filiere?.libelle ?? 'Formation'}</h3>
            {row.filiere?.code ? (
              <p className="apprenant-mes-cours-card__code">{row.filiere.code}</p>
            ) : null}
          </div>
          <div className="apprenant-mes-cours-card__badges">
            {evaluationLabel ? (
              <Link
                to={`/app/inscription/${row.id}#evaluations`}
                className="apprenant-mes-cours-card__badge apprenant-mes-cours-card__badge--evaluation hover:opacity-90"
                onClick={(e) => e.stopPropagation()}
              >
                {evaluationLabel}
              </Link>
            ) : null}
            <span className={`apprenant-mes-cours-card__badge ${statutBadgeClass(row.statut)}`}>
              {formatInscriptionStatut(row.statut ?? '')}
            </span>
          </div>
        </header>

        {evaluationSummary.approachingDeadline && evaluationSummary.deadlineLabel ? (
          <p className="apprenant-mes-cours-card__deadline" role="status">
            <LucideSymbolIcon name="timer" tone="inherit" size={16} className="shrink-0" />
            <span>Échéance proche : {evaluationSummary.deadlineLabel}</span>
          </p>
        ) : null}

        <dl className="apprenant-mes-cours-card__meta">
          {row.cohorte?.code || row.cohorte?.libelle ? (
            <div className="apprenant-mes-cours-card__meta-row">
              <dt>
                <LucideSymbolIcon name="groups" tone="inherit" size={14} />
                Cohorte
              </dt>
              <dd>{row.cohorte.libelle || row.cohorte.code}</dd>
            </div>
          ) : null}
          {sessionLabel ? (
            <div className="apprenant-mes-cours-card__meta-row">
              <dt>
                <LucideSymbolIcon name="event" tone="inherit" size={14} />
                Session
              </dt>
              <dd>{sessionLabel}</dd>
            </div>
          ) : null}
          {row.matriculeSnapshot ? (
            <div className="apprenant-mes-cours-card__meta-row">
              <dt>
                <LucideSymbolIcon name="badge" tone="inherit" size={14} />
                Matricule
              </dt>
              <dd className="font-mono text-xs">{row.matriculeSnapshot}</dd>
            </div>
          ) : null}
          {inscritLe ? (
            <div className="apprenant-mes-cours-card__meta-row">
              <dt>
                <LucideSymbolIcon name="calendar_month" tone="inherit" size={14} />
                Inscrit le
              </dt>
              <dd>{inscritLe}</dd>
            </div>
          ) : null}
        </dl>

        {total > 0 ? (
          <div className="apprenant-mes-cours-card__payment">
            <div className="flex justify-between text-xs">
              <span className="font-semibold text-on-surface-variant">Paiement</span>
              <span className="font-bold text-slate-900">
                {formatMoney(paid, 'CDF')} / {formatMoney(total, 'CDF')}
              </span>
            </div>
            <div className="apprenant-mes-cours-card__progress" aria-hidden>
              <div
                className={`apprenant-mes-cours-card__progress-bar ${ratio >= 100 ? 'apprenant-mes-cours-card__progress-bar--done' : ''}`}
                style={{ width: `${ratio}%` }}
              />
            </div>
            {reste > 0 ? (
              <p className="mt-1 text-xs font-semibold text-amber-700">Reste {formatMoney(reste, 'CDF')}</p>
            ) : (
              <p className="mt-1 text-xs font-medium text-emerald-700">Soldé</p>
            )}
          </div>
        ) : null}

        {row.id ? (
          <footer className="apprenant-mes-cours-card__actions">
            <Link
              to={`/app/inscription/${row.id}`}
              className="apprenant-mes-cours-card__btn apprenant-mes-cours-card__btn--outline"
            >
              <LucideSymbolIcon name="visibility" tone="inherit" size={16} />
              Détail
            </Link>
            {canPay && paymentNav ? (
              <Link
                to={`/app/inscription/${row.id}/paiement`}
                state={paymentNav}
                className="apprenant-mes-cours-card__btn apprenant-mes-cours-card__btn--primary"
              >
                <LucideSymbolIcon name="payments" tone="inherit" size={16} className="text-white" />
                {paymentNav.focusInscription ? "Frais d'inscription" : 'Payer'}
              </Link>
            ) : null}
          </footer>
        ) : null}
      </div>
    </article>
  );
}

export default function ApprenantMesCoursPanel() {
  const [items, setItems] = useState<InscriptionRow[]>([]);
  const [evaluationSummaries, setEvaluationSummaries] = useState<
    Record<string, EvaluationInscriptionSummary>
  >({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const loadEvaluationSummaries = async (inscriptions: InscriptionRow[]) => {
    const ids = inscriptions.map((row) => row.id).filter((id): id is string => Boolean(id));
    if (ids.length === 0) {
      setEvaluationSummaries({});
      return;
    }
    const entries = await Promise.all(
      ids.map(async (id) => {
        try {
          const rows = await fetchInscriptionEvaluations(id);
          return [id, summarizeInscriptionEvaluations(Array.isArray(rows) ? rows : [])] as const;
        } catch {
          return [id, EMPTY_EVALUATION_SUMMARY] as const;
        }
      }),
    );
    setEvaluationSummaries(Object.fromEntries(entries));
  };

  const load = async () => {
    setError(false);
    setLoading(true);
    try {
      const data = await fetchMyInscriptions();
      const list = Array.isArray(data) ? data : [];
      setItems(list);
      await loadEvaluationSummaries(list);
    } catch {
      setError(true);
      setEvaluationSummaries({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useRegisterPortalRefresh(() => void load());

  const enrolledItems = useMemo(
    () => items.filter((row) => isInscriptionEffectivelyEnrolled(row)),
    [items],
  );

  const pendingPaymentItems = useMemo(
    () => items.filter((row) => isInscriptionPendingSubscriptionPayment(row)),
    [items],
  );

  const {
    paginatedItems,
    currentPage,
    totalPages,
    onPageChange,
    summary,
  } = useAdminTablePagination(enrolledItems, MES_COURS_PAGE_SIZE);

  const stats = useMemo(() => computeMesCoursEnrollmentStats(items), [items]);

  return (
    <ApprenantPageShell
      title="Mes cours"
      description="Toutes les formations et cohortes auxquelles vous êtes inscrit(e). Suivez le statut et complétez vos paiements si nécessaire."
      sideCard={{
        label: 'Inscriptions actives',
        value: loading ? '…' : String(stats.actifs),
        helper:
          stats.enAttente > 0
            ? `${stats.enAttente} paiement${stats.enAttente > 1 ? 's' : ''} à finaliser · ${stats.total} au total`
            : `${stats.total} inscription${stats.total > 1 ? 's' : ''} au total`,
        sideLinks: [
          { label: 'Accueil', to: '/app', icon: 'dashboard' },
          { label: 'Formations', to: '/app/formations', icon: 'school' },
        ],
      }}
      onRetry={() => void load()}
      isLoading={loading}
      isError={error}
      loadingMessage="Chargement de vos cours…"
      errorMessage="Impossible de charger vos cours."
      primaryAction={
        enrolledItems.length === 0 && pendingPaymentItems.length === 0 && !loading && !error ? (
          <Link
            to="/app/formations"
            className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-xs font-bold text-primary"
          >
            <LucideSymbolIcon name="school" tone="inherit" size={16} />
            Parcourir les formations
          </Link>
        ) : undefined
      }
    >
      {!loading && !error && enrolledItems.length === 0 && pendingPaymentItems.length === 0 ? (
        <div className="apprenant-mes-cours-empty">
          <span className="apprenant-mes-cours-empty__icon">
            <LucideSymbolIcon name="school" tone="primary" size={40} />
          </span>
          <p className="apprenant-mes-cours-empty__text">
            Vous n&apos;êtes inscrit(e) à aucun cours pour le moment.
          </p>
          <Link to="/app/formations" className="apprenant-mes-cours-empty__cta">
            <LucideSymbolIcon name="school" tone="inherit" size={18} className="text-white" />
            Parcourir les formations
          </Link>
        </div>
      ) : !loading && !error ? (
        <>
          {pendingPaymentItems.length > 0 ? (
            <section className="apprenant-mes-cours-pending" aria-labelledby="apprenant-mes-cours-pending-heading">
              <header className="apprenant-mes-cours-list__header">
                <div className="min-w-0">
                  <h2 id="apprenant-mes-cours-pending-heading" className="apprenant-mes-cours-list__title">
                    Paiement à finaliser
                  </h2>
                  <p className="apprenant-mes-cours-list__subtitle">
                    Réglez les frais de souscription pour confirmer votre inscription.
                  </p>
                </div>
              </header>
              <ul className="apprenant-mes-cours-pending__list">
                {pendingPaymentItems.map((row) => (
                  <li key={row.id ?? row.matriculeSnapshot}>
                    <Link
                      to={row.id ? `/app/inscription/${row.id}` : '/app/formations'}
                      className="apprenant-mes-cours-pending__item"
                    >
                      <LucideSymbolIcon name="payments" tone="primary" size={20} />
                      <span className="min-w-0 flex-1">
                        <span className="block font-semibold text-slate-900">
                          {row.filiere?.libelle ?? 'Formation'}
                        </span>
                        <span className="block text-xs text-on-surface-variant">
                          Frais de souscription à régler
                          {row.cohorte?.code ? ` — ${row.cohorte.code}` : ''}
                        </span>
                      </span>
                      <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

        <section className="apprenant-mes-cours-list" aria-labelledby="apprenant-mes-cours-heading">
          <header className="apprenant-mes-cours-list__header">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <span className="apprenant-mes-cours-list__header-icon" aria-hidden>
                <LucideSymbolIcon name="list_alt" tone="inherit" size={22} />
              </span>
              <div className="min-w-0">
                <h2 id="apprenant-mes-cours-heading" className="apprenant-mes-cours-list__title">
                  Vos formations
                </h2>
                <p className="apprenant-mes-cours-list__subtitle">
                  {stats.actifs} active{stats.actifs !== 1 ? 's' : ''}
                  {stats.enAttente > 0
                    ? ` · ${stats.enAttente} paiement${stats.enAttente > 1 ? 's' : ''} en attente`
                    : ''}
                  {stats.total > 0 ? ` · ${stats.total} au total` : ''}
                </p>
              </div>
            </div>
            <Link to="/app/formations" className="apprenant-mes-cours-list__header-link">
              Nouvelle inscription
              <LucideSymbolIcon name="arrow_forward" tone="inherit" size={16} />
            </Link>
          </header>
          <div className="apprenant-mes-cours-list__body">
            <div className="apprenant-mes-cours-list__grid">
              {paginatedItems.map((row) => (
                <MesCoursCard
                  key={row.id ?? row.matriculeSnapshot}
                  row={row}
                  evaluationSummary={
                    row.id ? evaluationSummaries[row.id] ?? EMPTY_EVALUATION_SUMMARY : EMPTY_EVALUATION_SUMMARY
                  }
                />
              ))}
            </div>
            {enrolledItems.length > 0 ? (
              <AdminPagination
                className="apprenant-mes-cours-list__pagination"
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={onPageChange}
                summary={summary}
              />
            ) : enrolledItems.length === 0 && pendingPaymentItems.length > 0 ? (
              <p className="apprenant-mes-cours-list__subtitle">
                Aucun cours confirmé pour le moment. Finalisez le paiement de vos souscriptions ci-dessus.
              </p>
            ) : null}
          </div>
        </section>
        </>
      ) : null}
    </ApprenantPageShell>
  );
}
